/**
 * NetworkPicker — searchable dropdown of Meraki networks.
 *
 * Replaces the legacy `MerakiNetworkSelector` Django form. Used by every page
 * that needs the user to pick a single network (debranding, onboarding,
 * provisioning, etc).
 *
 * Reads from the meraki-networks store; lazily triggers a load on first open.
 */
import { useMemo, useState } from "react";
import { ChevronDown, Network as NetIcon, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { useMerakiNetworks } from "@/stores/meraki-networks";
import type { MerakiNetwork } from "@/api/schemas/meraki";
import { cn } from "@/lib/cn";

export interface NetworkPickerProps {
  value: MerakiNetwork | null;
  onChange: (n: MerakiNetwork) => void;
  className?: string;
  /** Override the trigger label. */
  placeholder?: string;
}

export function NetworkPicker({
  value,
  onChange,
  className,
  placeholder = "Select a Meraki network…",
}: NetworkPickerProps) {
  const networks = useMerakiNetworks((s) => s.data);
  const status = useMerakiNetworks((s) => s.status);
  const load = useMerakiNetworks((s) => s.load);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Filter — match against name + organization. Case-insensitive.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return networks;
    return networks.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        n.organizationName.toLowerCase().includes(q),
    );
  }, [networks, query]);

  return (
    <DropdownMenu open={open} onOpenChange={(next) => { setOpen(next); if (next && status === "idle") void load(); }}>
      <DropdownMenuTrigger
        className={cn(
          "flex h-10 w-full items-center justify-between gap-3 rounded-xl border border-line bg-surface px-3.5 text-sm text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <NetIcon size={14} className="shrink-0 text-fg-mute" />
          {value ? (
            <span className="flex min-w-0 flex-col items-start text-left leading-tight">
              <span className="truncate font-semibold text-fg">{value.name}</span>
              <span className="truncate text-[10.5px] text-fg-mute">
                {value.organizationName} · {value.deviceCount ?? 0} devices
              </span>
            </span>
          ) : (
            <span className="text-fg-mute">{placeholder}</span>
          )}
        </span>
        <ChevronDown size={14} className="shrink-0 text-fg-mute" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[420px] p-0">
        <div className="border-b border-line p-2">
          <div className="relative">
            <Search
              size={13}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by network or organization…"
              className="h-9 pl-8 text-xs"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {status === "loading" && networks.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-fg-mute">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-fg-mute">
              {query ? `No networks match "${query}"` : "No networks available"}
            </div>
          ) : (
            visible.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  onChange(n);
                  setOpen(false);
                  setQuery("");
                }}
                className="flex w-full items-start gap-2.5 rounded-md px-3 py-2 text-left text-xs hover:bg-aurora-soft"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-fg">{n.name}</div>
                  <div className="mt-0.5 truncate text-[11px] text-fg-mute">
                    {n.organizationName}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="font-mono text-[10px] text-fg-mute">
                    {n.deviceCount ?? 0} dev
                  </span>
                  <div className="flex flex-wrap justify-end gap-0.5">
                    {n.productTypes.map((p) => (
                      <Badge key={p} variant="neutral" size="sm">
                        {p}
                      </Badge>
                    ))}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
